import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import DaumPostcodeEmbed from "react-daum-postcode";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "components/ui/drawer";
import { useMediaQuery } from "hooks/utils/useMediaQuery";
import Input from "./common/Input";
import Button from "./common/Button";
import AddressBook from "./AddressBook";
import { useMorningAuthStore } from "store/useMorningAuthStore";
import { useGetOrderSearch, OrderSearchItem } from "../../../../hooks/morning/useGetOrderSearch";
import { useMasterAuthStore } from "../../../../store/useMasterAuthStore";
import { ReactComponent as Close } from "assets/morning/icon/close.svg";
import { ReactComponent as Checked } from "assets/morning/icon/checked.svg";
import { ReactComponent as ToolTip } from "assets/morning/icon/toolTip.svg";
import { X } from "lucide-react";
import CheckBox from "./common/Checkbox";
import FileDropZone from "./common/FileDropZone";
import { AddressItem } from "hooks/morning/usePostAddressBook";
import { usePostAddressBook } from "hooks/morning/usePostAddressBook";

const addressInfo = (
    <>
        <div className="absolute -top-2 left-6 w-3 h-3 bg-white border-l border-t border-gray-300 rotate-45 responsive700:hidden" />

        <p>기본출발지주소설정</p>
    </>
);
const formatMobileNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 11);
    if (cleaned.length < 4) return cleaned;
    if (cleaned.length < 8) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
};

interface AddressInfoProps {
    modify?: boolean;
    depart: any;
    setDepart: Function;
    arrive: any;
    setArrive: Function;
    id?: string;
    master?: boolean;
    infoData: any;
    errorMessages?: any;
    isSubmitting?: boolean;
    receiptData: any;
    pickup: boolean;
    isArriveUseDefault: boolean;
    setIsArriveUseDefault: Function;
    payMethodData: {
        pickup: number;
        payment: number;
        count?: string;
        weight?: string;
    };
    onWeightChange?: (weight: string) => void;
    c_month_yn?: string;
    pickupFiles?: File[];
    onPickupFilesChange?: (files: File[]) => void;
}

const AddressInfo = forwardRef(
    (
        {
            modify = false,
            depart,
            setDepart,
            arrive,
            setArrive,
            id,
            master,
            infoData,
            errorMessages,
            isSubmitting,
            receiptData,
            pickup,
            isArriveUseDefault,
            setIsArriveUseDefault,
            payMethodData,
            onWeightChange,
            c_month_yn,
            pickupFiles,
            onPickupFilesChange,
        }: AddressInfoProps,
        ref,
    ) => {
        // console.log("depart", depart);
        const navigate = useNavigate();
        const departRef = useRef<HTMLDivElement>(null);
        const arriveRef = useRef<HTMLDivElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const scrollTarget = useRef<"depart" | "arrive" | null>(null);
        const addressBookTarget = useRef<"depart" | "arrive">("arrive");
        const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
        const arriveItemRefs = useRef<(HTMLDivElement | null)[]>([]);
        const drawerTriggerRef = useRef<HTMLButtonElement | null>(null);
        const c_code = useMorningAuthStore(state => state.c_code);
        const c_company = useMorningAuthStore(state => state.c_company);
        const c_tel = useMorningAuthStore(state => state.c_tel);
        const c_rtel = useMorningAuthStore(state => state.c_rtel);
        const c_setel = useMorningAuthStore(state => state.c_setel);
        const masterCCompany = useMasterAuthStore(state => state.c_company);
        const masterStoreCCode = useMasterAuthStore(state => state.c_code ?? "");
        const useCode = (!master && c_code) || (master && masterStoreCCode);
        const [showDepartPostcode, setShowDepartPostcode] = useState(false);
        const [showArrivePostcode, setShowArrivePostcode] = useState(false);
        const [showAddressBook, setShowAddressBook] = useState(false);
        const [companyNameChange, setCompanyNameChange] = useState<boolean>(false);
        const [departIsFocused, setDepartIsFocused] = useState(false);
        const [arriveIsFocused, setArriveIsFocused] = useState(false);
        const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
        const [focusedIndex, setFocusedIndex] = useState<number>(-1);
        const [arriveFocusedIndex, setArriveFocusedIndex] = useState<number>(-1);
        const isPostcodeMatch = [infoData?.C_Post, infoData?.C_RPost, infoData?.C_SePost].includes(arrive.postcode);

        const { data: departSearchData } = useGetOrderSearch(depart.displayName, { enabled: pickup ? false : true });
        // const { data: arriveSearchData } = useGetOrderSearch(arrive.name);

        // 외부 픽업 일 때 출발지 주소록 검색
        const [addressBookSearchData, setAddressBookSearchData] = useState<AddressItem[]>([]);
        const { mutate: departAddressBookSearchMutate } = usePostAddressBook((data: AddressItem[]) => {
            setAddressBookSearchData(data);
        });
        // 도착지 주소록 검색
        const [arriveSearchData, setArriveSearchData] = useState<AddressItem[]>([]);
        const { mutate: arriveAddressBookSearchMutate } = usePostAddressBook((data: AddressItem[]) => {
            setArriveSearchData(data);
        });

        const responsive700 = useMediaQuery(700);
        const postcodePrefix = parseInt(depart.postcode.slice(0, 2), 10);
        const [checkedLinkPrint, setCheckedLinkPrint] = useState(false);

        const isDaeguGyeongbukBusanGyeongNam =
            (postcodePrefix >= 36 && postcodePrefix <= 43) || // 경북 + 대구
            (postcodePrefix >= 46 && postcodePrefix <= 49) || // 부산
            (postcodePrefix >= 50 && postcodePrefix <= 53); // 경남

        const arrivePrefix = parseInt(arrive.postcode.slice(0, 2), 10);
        const isDepartDaegu = postcodePrefix >= 39 && postcodePrefix <= 43;
        const isArriveDaegu = arrivePrefix >= 39 && arrivePrefix <= 43;
        const isArriveSeoul = arrivePrefix >= 1 && arrivePrefix <= 8;
        const showMonthlyWeight = c_month_yn === "Y" && payMethodData.pickup === 1 && (
            (isDepartDaegu && (isArriveSeoul || isArriveDaegu)) ||
            (!isDepartDaegu && isArriveDaegu)
        );

        useImperativeHandle(ref, () => ({
            validate: () => {
                const newErrors: { [key: string]: boolean } = {};
                let errorTarget: "depart" | "arrive" | null = null;

                if (!depart.displayName.trim()) {
                    newErrors.departName = true;
                    errorTarget = "depart";
                }
                if (!depart.tel.trim()) {
                    newErrors.departTel = true;
                    if (!errorTarget) errorTarget = "depart";
                }
                if (!useCode && (!depart.postcode.trim() || !depart.address.trim())) {
                    newErrors.departPostcode = !depart.postcode.trim();
                    newErrors.departAddress = !depart.address.trim();
                    if (!errorTarget) errorTarget = "depart";
                }
                if (!arrive.name.trim()) {
                    newErrors.arriveName = true;
                    if (!errorTarget) errorTarget = "arrive";
                }
                if (!arrive.tel.trim()) {
                    newErrors.arriveTel = true;
                    if (!errorTarget) errorTarget = "arrive";
                }
                if (
                    (payMethodData.pickup === 1 && payMethodData.payment === 3) ||
                    (payMethodData.pickup === 2 && payMethodData.payment === 3)
                ) {
                    if (!arrive.manager?.trim()) {
                        newErrors.arriveManager = true;
                        if (!errorTarget) errorTarget = "arrive";
                    }
                }
                if (!arrive.postcode.trim() || !arrive.address.trim()) {
                    newErrors.arrivePostcode = !arrive.postcode.trim();
                    newErrors.arriveAddress = !arrive.address.trim();
                    if (!errorTarget) errorTarget = "arrive";
                }
                if ((!useCode || pickup || (useCode && !depart.damCode)) && (!depart.postcode.trim() || !depart.address.trim())) {
                    newErrors.departPostcode = !depart.postcode.trim();
                    newErrors.departAddress = !depart.address.trim();
                    if (!errorTarget) errorTarget = "depart";
                }
                if (isDaeguGyeongbukBusanGyeongNam) {
                    const totalCOunt = Number(depart.roll) + Number(depart.box) + Number(depart.sample);
                    if (Number(payMethodData.count) !== totalCOunt) {
                        newErrors.roll = true;
                        newErrors.box = true;
                        newErrors.sample = true;
                        if (!errorTarget) errorTarget = "arrive";
                    }
                }

                if (showMonthlyWeight && !(payMethodData.weight || "").trim()) {
                    newErrors.weight = true;
                    if (!errorTarget) errorTarget = "arrive";
                }

                // 외부 픽업 & 체크박스 선택시 휴대폰번호 입력 필수
                if (pickup && checkedLinkPrint && !depart.mobile.trim()) {
                    newErrors.departMobile = true;
                    if (!errorTarget) errorTarget = "depart";
                }

                scrollTarget.current = errorTarget;
                setErrors(newErrors);
                return Object.keys(newErrors).length === 0;
            },
            scrollIntoView: () => {
                const targetRef = scrollTarget.current === "depart" ? departRef : arriveRef;
                targetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                setTimeout(() => window.scrollBy({ top: -100, behavior: "smooth" }), 200);
            },
            resetCompanyNameChange: () => {
                setCompanyNameChange(false);
            },
            clearErrors: () => {
                // ✅ 추가
            },
        }));

        const handleDepartAddress = (data: any) => {
            let fullAddress = data.roadAddress || data.jibunAddress;

            const extras: string[] = [];

            if (data.bname) extras.push(data.bname);

            if (extras.length > 0) {
                fullAddress += ` (${extras.join(", ")})`;
            }

            setDepart((prev: any) => ({
                ...prev,
                postcode: data.zonecode,
                address: fullAddress,
                roll: "",
                box: "",
                sample: "",
            }));

            setErrors(prev => ({
                ...prev,
                departPostcode: false,
                departAddress: false,
            }));

            setShowDepartPostcode(false);
        };

        const handleArriveAddress = (data: any) => {
            let fullAddress = data.roadAddress || data.jibunAddress;

            const extras: string[] = [];

            if (data.bname) extras.push(data.bname);

            if (extras.length > 0) {
                fullAddress += ` (${extras.join(", ")})`;
            }

            setArrive((prev: any) => ({
                ...prev,
                postcode: data.zonecode,
                address: fullAddress,
            }));

            setErrors(prev => ({
                ...prev,
                arrivePostcode: false,
                arriveAddress: false,
            }));

            setShowArrivePostcode(false);
        };

        const handleAddressSelect = (item: any, type: "depart" | "arrive") => {
            if (type === "depart") {
                setDepart({
                    ...depart,
                    displayName: item.a_company || "",
                    name: item.a_company || "",
                    tel: item.a_tel || "",
                    manager: item.a_name || "",
                    postcode: item.a_post || "",
                    address: item.a_address || "",
                    addressDetail: item.a_address_detail || "",
                    mobile: item.a_mobile || "",
                    f_c_code: item.a_code || "",
                    roll: "",
                    box: "",
                    sample: "",
                });

                setErrors(prev => ({
                    ...prev,
                    departName: false,
                    departTel: false,
                    departPostcode: false,
                    departAddress: false,
                    roll: false,
                    box: false,
                    sample: false,
                }));
            } else {
                // console.log(item);
                setArrive({
                    ...arrive,
                    name: item.a_company || "",
                    tel: item.a_tel || "",
                    manager: item.a_name || "",
                    postcode: item.a_post || "",
                    address: item.a_address || "",
                    addressDetail: item.a_address_detail || "",
                    t_c_code: item.a_code || "",
                    mobile: item.a_mobile || "",
                });

                setErrors(prev => ({
                    ...prev,
                    arriveName: false,
                    arriveTel: false,
                    arrivePostcode: false,
                    arriveAddress: false,
                    ...(item.a_name && { arriveManager: false }),
                }));
            }
            setShowAddressBook(false);
        };

        const handleSelect = (key: string) => {
            const idx = Number(key) - 1;
            if (!infoData) return;

            const addr1 = [infoData.C_ADDR1, infoData.C_RADDR1, infoData.C_SeADDR1][idx];
            const addr2 = [infoData.C_ADDR2, infoData.C_RADDR2, infoData.C_SeADDR2][idx];
            const postcode = [infoData.C_Post, infoData.C_RPost, infoData.C_SePost][idx];
            const branch = [infoData.C_Branch, infoData.C_RBranch, infoData.C_SeBranch][idx];
            const tel = { "1": c_tel, "2": c_rtel, "3": c_setel }[key] ?? "";

            const dcodeMap: Record<string, string | undefined> = {
                "1": infoData.dcode,
                "2": infoData.dcode1,
                "3": infoData.dcode2,
            };
            const damCode = dcodeMap[key] ?? "";

            setDepart((prev: any) => ({
                ...prev,
                damCode: damCode,
                tel: tel,
                address: addr1 || "",
                addressDetail: addr2 || "",
                postcode: postcode || "",
                value: branch,
                roll: "",
                box: "",
                sample: "",
            }));
        };

        const handleSameToDepart = () => {
            setDepart((prev: any) => ({
                ...prev,
                mobile: c_code ? receiptData.tel : prev.mobile,
                tel: !c_code ? receiptData.tel : prev.tel,
                manager: receiptData.manager,
                // name: c_company || receiptData.name,
                displayName: c_company || receiptData.name,
                f_c_code: c_code ?? prev.f_c_code,
            }));
        };

        const handleSameToArrive = () => {
            setArrive((prev: any) => ({
                ...prev,
                mobile: receiptData.tel,
                manager: receiptData.manager,
                name: c_company,
                tel: c_tel,
                t_c_code: c_code,
            }));
            // 담당지점/전화/주소는 부모가 회원 기본 주소로 이어서 채운다
            setIsArriveUseDefault(true);
        };

        useEffect(() => {
            if (master) {
                if (masterStoreCCode) {
                    setDepart((prev: any) => ({ ...prev, name: masterCCompany, displayName: masterCCompany }));
                } else {
                    setDepart((prev: any) => ({ ...prev, name: "", displayName: "" }));
                }
            }
            if (c_company) {
                setDepart((prev: any) => ({ ...prev, name: c_company, displayName: c_company }));
            } else {
                setDepart((prev: any) => ({ ...prev, name: "", displayName: "" }));
            }
        }, [c_company, master, masterStoreCCode]);

        useEffect(() => {
            if (!infoData) return;
            if (!c_code) return;

            const defaultCode = infoData.c_addr_defalt || "1";
            const idx = Number(defaultCode) - 1;

            const addr1 = [infoData.C_ADDR1, infoData.C_RADDR1, infoData.C_SeADDR1][idx];
            const addr2 = [infoData.C_ADDR2, infoData.C_RADDR2, infoData.C_SeADDR2][idx];
            const postcode = [infoData.C_Post, infoData.C_RPost, infoData.C_SePost][idx];

            setDepart((prev: any) => {
                if (prev.damCode) return prev;

                return {
                    ...prev,
                    damCode: defaultCode,
                    address: addr1 || "",
                    addressDetail: addr2 || "",
                    postcode: postcode || "",
                };
            });
        }, [infoData]);

        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                    setDepartIsFocused(false);
                    setArriveIsFocused(false);
                }
            };

            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, []);

        const onClickaddressInfoToolTip = () => {
            if (responsive700) drawerTriggerRef.current?.click();
            else navigate("/mypage");
        };

        useEffect(() => {
            if (master && !masterStoreCCode) {
                setDepart((prev: any) => {
                    setDepart({ ...prev, name: "", displayName: "", address: "", postcode: "", addressDetail: "" });
                });
            } else if (master && masterStoreCCode) {
                setDepart((prev: any) => {
                    setDepart({ ...prev, name: masterCCompany, displayName: masterCCompany });
                });
            }
        }, []);

        // 외부 픽업 일 때 출발지 주소록 검색 데이터
        const departSearchDataList = pickup ? addressBookSearchData : departSearchData;

        const departSubmitHandler = (data: { name: string; tel: string; postcode: string; address: string; addressDetail: string }) => {
            setDepart((prev: any) => ({
                ...prev,
                ...data,
            }));

            setDepartIsFocused(false);
        };

        // console.log("focusedIndex:::", focusedIndex);
        const isHandlingKey = useRef(false);
        const isHandlingArriveKey = useRef(false);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (isHandlingKey.current) return;
            isHandlingKey.current = true;
            setTimeout(() => {
                isHandlingKey.current = false;
            }, 50);

            if (!departSearchDataList || departSearchDataList.length === 0) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                setFocusedIndex(prev => {
                    if (prev === -1) return 0;
                    if (prev < departSearchDataList.length - 1) return prev + 1;
                    return 0;
                });
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                setFocusedIndex(prev => {
                    if (prev > 0) return prev - 1;
                    return departSearchDataList.length - 1;
                });
            } else if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (focusedIndex >= 0 && focusedIndex < departSearchDataList.length) {
                    const selected = departSearchDataList[focusedIndex];
                    // console.log('depart selected',selected);
                    const isAddressItem = pickup && "a_code" in selected;

                    setDepart((prev: any) => ({
                        ...prev,
                        displayName: isAddressItem
                            ? (selected as AddressItem).a_company || ""
                            : (selected as OrderSearchItem).C_Company || "",
                        name: isAddressItem ? (selected as AddressItem).a_company || "" : (selected as OrderSearchItem).C_Company || "",
                        tel: isAddressItem ? (selected as AddressItem).a_tel || "" : (selected as OrderSearchItem).c_tel || "",
                        postcode: isAddressItem ? (selected as AddressItem).a_post || "" : (selected as OrderSearchItem).c_post || "",
                        address: isAddressItem ? (selected as AddressItem).a_address || "" : (selected as OrderSearchItem).C_ADDR1 || "",
                        addressDetail: isAddressItem
                            ? (selected as AddressItem).a_address_detail || ""
                            : (selected as OrderSearchItem).C_ADDR2 || "",
                        f_c_code: isAddressItem ? (selected as AddressItem).a_code || "" : (selected as OrderSearchItem).c_code || "",
                    }));
                    setDepartIsFocused(false);
                    setFocusedIndex(-1);
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setDepartIsFocused(false);
                setFocusedIndex(-1);
            }
        };

        const handleArriveKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (isHandlingArriveKey.current) return;
            isHandlingArriveKey.current = true;
            setTimeout(() => {
                isHandlingArriveKey.current = false;
            }, 50);

            if (!arriveSearchData || arriveSearchData.length === 0) return;

            if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                setArriveFocusedIndex(prev => {
                    if (prev === -1) return 0;
                    if (prev < arriveSearchData.length - 1) return prev + 1;
                    return 0;
                });
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                setArriveFocusedIndex(prev => {
                    if (prev > 0) return prev - 1;
                    return arriveSearchData.length - 1;
                });
            } else if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (arriveFocusedIndex >= 0 && arriveFocusedIndex < arriveSearchData.length) {
                    const selected = arriveSearchData[arriveFocusedIndex];
                    // console.log("arrive selected", selected);
                    setArrive((prev: any) => ({
                        ...prev,
                        name: selected.a_company || "",
                        tel: selected.a_tel || "",
                        postcode: selected.a_post || "",
                        address: selected.a_address || "",
                        addressDetail: selected.a_address_detail || "",
                        t_c_code: selected.a_code || "",
                    }));
                    setArriveIsFocused(false);
                    setArriveFocusedIndex(-1);
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setArriveIsFocused(false);
                setArriveFocusedIndex(-1);
            }
        };

        useEffect(() => {
            if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
                itemRefs.current[focusedIndex]?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                });
            }
        }, [focusedIndex]);

        useEffect(() => {
            if (arriveFocusedIndex >= 0 && arriveItemRefs.current[arriveFocusedIndex]) {
                arriveItemRefs.current[arriveFocusedIndex]?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                });
            }
        }, [arriveFocusedIndex]);

        // 외부 픽업 일 때 체크박스 상태 업데이트
        useEffect(() => {
            if (pickup) {
                const newChecked = depart.linkPrint === "Y" ? true : false;
                setCheckedLinkPrint(newChecked);
            } else {
                setCheckedLinkPrint(false);
            }
        }, [pickup]);

        return (
            <>
                <div
                    ref={containerRef}
                    id={id}
                    className={`${modify ? "w-full max-w-[680px] flex-col" : "w-full responsive1200:flex-col"} flex gap-5`}
                >
                    <div ref={departRef} className="bg-white flex-1 rounded-2xl p-10 responsive1200:p-[16px]">
                        <div className="border-b-[2px] justify-between border-black flex items-center pb-2">
                            <div className="text-[28px] font-bold responsive1200:text-[20px] whitespace-nowrap mr-4">출발지 정보</div>
                            {pickup ? (
                                <div className="flex justify-between w-full">
                                    <Button
                                        onClick={() => {
                                            setDepart((prev: any) => ({
                                                ...prev,
                                                name: "",
                                                displayName: "",
                                                tel: "",
                                                manager: "",
                                                mobile: "",
                                                postcode: "",
                                                address: "",
                                                addressDetail: "",
                                                f_c_code: "",
                                            }));
                                            setShowDepartPostcode(false);
                                        }}
                                    >
                                        주소리셋
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowAddressBook(true);
                                            addressBookTarget.current = "depart";
                                        }}
                                        color="new"
                                    >
                                        주소록
                                    </Button>
                                </div>
                            ) : (
                                <Button onClick={handleSameToDepart}>접수자 정보 동일</Button>
                            )}
                        </div>
                        <div>
                            <div
                                className={`flex items-center py-5 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]`}
                            >
                                <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                    상호명 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className="flex flex-col w-full gap-2">
                                    {useCode && !pickup ? (
                                        companyNameChange ? (
                                            <div className="flex w-full gap-1">
                                                <Input
                                                    className="w-[265px]"
                                                    value={depart.displayName}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        setDepart((prev: any) => ({ ...prev, displayName: value }));

                                                        if (isSubmitting) {
                                                            let isInValidValue: boolean;
                                                            if (value.trim().length === 0) {
                                                                isInValidValue = true;
                                                            } else {
                                                                isInValidValue = false;
                                                            }
                                                            setErrors(prev => ({ ...prev, departName: isInValidValue }));
                                                        } else {
                                                            setErrors(prev => ({ ...prev, departName: false }));
                                                        }
                                                    }}
                                                    error={errors?.departName}
                                                />
                                                <Button
                                                    onClick={() => {
                                                        setCompanyNameChange(false);
                                                        // setDepart((prev: any) => ({
                                                        //     ...prev,
                                                        //     name: modify ? depart.name : depart.name,
                                                        //     f_c_code: c_code ?? "",
                                                        // }));
                                                        setDepart((prev: any) => ({ ...prev, displayName: depart.name }));
                                                    }}
                                                    className="!h-[52px] !w-[80px]"
                                                >
                                                    초기화
                                                </Button>
                                            </div>
                                        ) : (
                                            <div
                                                className={`flex h-[54px] items-center justify-between ${modify ? "w-full max-w-[440px]" : "responsive1200:w-full"}`}
                                            >
                                                <p className="text-[18px] font-[500] text-[#1B1C1E]">
                                                    {/* {modify ? depart.name : masterCCompany || c_company} */}
                                                    {depart.displayName}
                                                </p>
                                                <Button onClick={() => setCompanyNameChange(true)}>상호명변경</Button>
                                            </div>
                                        )
                                    ) : (
                                        <div className="relative w-full">
                                            <Input
                                                placeholder="상호명을 직접 입력하거나 조회하세요."
                                                value={depart.displayName ?? ""}
                                                onChange={e => {
                                                    const value = e.target.value;

                                                    // setDepart((prev: any) => ({ ...prev, name: value }));
                                                    setDepart((prev: any) => ({ ...prev, displayName: value }));

                                                    // 외부 픽업 일 때 주소록 검색
                                                    if (pickup) {
                                                        departAddressBookSearchMutate({ searchText: value });
                                                    }

                                                    setDepartIsFocused(true);

                                                    if (isSubmitting) {
                                                        let isInValidValue: boolean;
                                                        if (value.trim().length === 0) {
                                                            isInValidValue = true;
                                                        } else {
                                                            isInValidValue = false;
                                                        }
                                                        setErrors(prev => ({ ...prev, departName: isInValidValue }));
                                                    } else {
                                                        setErrors(prev => ({ ...prev, departName: false }));
                                                    }
                                                }}
                                                error={errors?.departName}
                                                onFocus={() => setDepartIsFocused(true)}
                                                onKeyDown={handleKeyDown}
                                                className={`${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                            />
                                            {departIsFocused &&
                                                depart.displayName?.length >= 1 &&
                                                departSearchDataList &&
                                                departSearchDataList.length > 0 && (
                                                    <div
                                                        className={`absolute top-[56px] left-0 z-10 border bg-white rounded-lg shadow w-full`}
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                setDepartIsFocused(false);
                                                                setFocusedIndex(-1);
                                                            }}
                                                            className="absolute right-0 top-0 w-6 h-6 hover:bg-black/10 flex justify-center items-center rounded-full"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                        <div
                                                            className={` max-h-[400px] overflow-y-auto ${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                                        >
                                                            {departSearchDataList?.map((company, index) => {
                                                                const isAddressItem = pickup && "a_code" in company;

                                                                const params = isAddressItem
                                                                    ? {
                                                                          name: (company as AddressItem).a_company,
                                                                          displayName: (company as AddressItem).a_company,
                                                                          tel: (company as AddressItem).a_tel,
                                                                          postcode: (company as AddressItem).a_post,
                                                                          address: (company as AddressItem).a_address,
                                                                          addressDetail: (company as AddressItem).a_address_detail || "",
                                                                          f_c_code: (company as AddressItem).a_code || "",
                                                                      }
                                                                    : {
                                                                          name: (company as OrderSearchItem).C_Company,
                                                                          displayName: (company as OrderSearchItem).C_Company,
                                                                          tel: (company as OrderSearchItem).c_tel,
                                                                          postcode: (company as OrderSearchItem).c_post,
                                                                          address: (company as OrderSearchItem).C_ADDR1,
                                                                          addressDetail: (company as OrderSearchItem).C_ADDR2,
                                                                          f_c_code: (company as OrderSearchItem).c_code,
                                                                      };

                                                                const displayInfo = isAddressItem
                                                                    ? `${(company as AddressItem).a_company} | ${(company as AddressItem).a_tel} | ${(company as AddressItem).a_address}`
                                                                    : (company as OrderSearchItem).display_info;

                                                                const key = isAddressItem
                                                                    ? `${(company as AddressItem).a_code}-${index}`
                                                                    : `${(company as OrderSearchItem).c_code}-${index}`;

                                                                return (
                                                                    <div
                                                                        key={key}
                                                                        ref={el => (itemRefs.current[index] = el)}
                                                                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-[16px] ${
                                                                            focusedIndex === index ? "bg-gray-200" : "hover:bg-gray-100"
                                                                        }`}
                                                                        onClick={() => {
                                                                            departSubmitHandler(params);
                                                                        }}
                                                                    >
                                                                        {displayInfo}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                    {errors?.departName && <p className="text-[15px] text-[#ff4242]">{errorMessages?.departName}</p>}
                                </div>
                            </div>
                            <div className="flex items-center mb-5 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                    연락처 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                    <Input
                                        placeholder="연락처를 입력해주세요."
                                        className="w-full"
                                        value={depart.tel}
                                        onChange={e => {
                                            const rawValue = e.target.value;
                                            const limitedValue = rawValue.slice(0, 13);

                                            setDepart((prev: any) => ({ ...prev, tel: limitedValue }));

                                            if (isSubmitting) {
                                                const isInvalid = limitedValue.trim().length === 0;
                                                setErrors(prev => ({ ...prev, departTel: isInvalid }));
                                            } else {
                                                setErrors(prev => ({ ...prev, departTel: false }));
                                            }
                                        }}
                                        error={errors?.departTel}
                                    />

                                    {errors?.departTel && <p className="text-[15px] text-[#ff4242]">{errorMessages?.departTel}</p>}
                                </div>
                            </div>
                            <div className="flex items-center mb-5 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                <div className="text-[18px] font-[500] min-w-[160px]">담당자명</div>
                                <Input
                                    placeholder="담당자명을 입력해주세요."
                                    className={`${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                    value={depart.manager}
                                    onChange={e => setDepart((prev: any) => ({ ...prev, manager: e.target.value }))}
                                />
                            </div>

                            <div className="flex items-start mb-5 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                <div className="text-[18px] font-[500] min-w-[160px] mt-3 responsive1200:mt-0">휴대폰 번호</div>
                                <div className="w-full">
                                    <Input
                                        placeholder="휴대폰 번호 입력해주세요."
                                        className={`${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                        value={depart.mobile}
                                        onChange={e => {
                                            const formatted = formatMobileNumber(e.target.value);
                                            setDepart((prev: any) => ({ ...prev, mobile: formatted }));

                                            if (isSubmitting) {
                                                const isInvalid = formatted.trim().length === 0;
                                                setErrors(prev => ({ ...prev, departMobile: isInvalid }));
                                            } else {
                                                setErrors(prev => ({ ...prev, departMobile: false }));
                                            }
                                        }}
                                        error={errors?.departMobile}
                                    />
                                    {errors?.departMobile && <p className="text-[15px] text-[#ff4242]">{errorMessages?.departMobile}</p>}

                                    {pickup && (
                                        <div className="text-new text-[15px] mt-2 ml-1 break-keep flex items-center cursor-pointer">
                                            <CheckBox
                                                size="w-10 h-10"
                                                checked={checkedLinkPrint}
                                                onChange={check => {
                                                    setCheckedLinkPrint(check);
                                                    setDepart((prev: any) => ({ ...prev, linkPrint: check ? "Y" : "N" }));
                                                }}
                                                customLabel={
                                                    <p className="text-new text-[14px] mt-2 ml-1 break-keep flex items-center gap-2 cursor-pointer">
                                                        체크시 출발지 휴대폰번호로 운송장을 출력할 수 있게 알림톡을
                                                        보내드립니다.(PC로만출력가능)
                                                    </p>
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col responsive1200:py-[10px]">
                                <div className="text-[18px] font-[500] mb-3 flex items-center gap-1">
                                    출발지 주소 <span className="text-new text-[16px] font-normal">*</span>
                                    <div className="relative group">
                                        <ToolTip className="cursor-pointer" onClick={onClickaddressInfoToolTip} />
                                        <div className="hidden group-hover:block w-[340px] absolute top-full -left-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-md px-4 py-3 text-[17px] text-[#51535B] z-10 responsive700:group-hover:hidden">
                                            {addressInfo}
                                        </div>
                                    </div>
                                </div>
                                {useCode && !!infoData?.C_ADDR1 && !pickup ? (
                                    <div className="flex flex-col border rounded-lg">
                                        {infoData && (
                                            <>
                                                {[
                                                    {
                                                        value: "1",
                                                        title: infoData.c_addr,
                                                        addr1: infoData.C_ADDR1,
                                                        addr2: infoData.C_ADDR2,
                                                        name: infoData.dname,
                                                        tel: infoData.dtel,
                                                        post: infoData.C_Post,
                                                        // branch: infoData.C_Branch,
                                                    },
                                                    {
                                                        value: "2",
                                                        title: infoData.c_raddr,
                                                        addr1: infoData.C_RADDR1,
                                                        addr2: infoData.C_RADDR2,
                                                        name: infoData.dname1,
                                                        tel: infoData.dtel1,
                                                        post: infoData.C_RPost,
                                                        // branch: infoData.C_RBranch,
                                                    },
                                                    {
                                                        value: "3",
                                                        title: infoData.c_seaddr,
                                                        addr1: infoData.C_SeADDR1,
                                                        addr2: infoData.C_SeADDR2,
                                                        name: infoData.dname2,
                                                        tel: infoData.dtel2,
                                                        post: infoData.C_SePost,
                                                        // branch: infoData.C_SeBranch,
                                                    },
                                                ]
                                                    .filter(item => item.addr1)
                                                    .map((item, i) => {
                                                        const isSelected =
                                                            depart.postcode === item.post &&
                                                            depart.address === item.addr1 &&
                                                            depart.addressDetail === item.addr2;

                                                        return (
                                                            <label
                                                                key={item.value}
                                                                className={`flex flex-col py-4 px-3  ${i < 2 ? "border-b" : ""} cursor-pointer ${
                                                                    isSelected ? "" : "bg-[#f7f7f8]"
                                                                }`}
                                                                onClick={() => handleSelect(item.value)}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {isSelected ? (
                                                                        <Checked className="w-8 h-8 text-new shrink-0" />
                                                                    ) : (
                                                                        <div className="w-[19px] h-[19px] border-gray-300 mx-[6.5px] rounded-full border-2 shrink-0" />
                                                                    )}
                                                                    <div className="flex flex-col">
                                                                        <p className="text-[18px] font-semibold text-[#1b1c1e] pb-1">
                                                                            {item.title}
                                                                        </p>
                                                                        <p className="text-[#5a5c63]">
                                                                            {item.addr1} {item.addr2}
                                                                        </p>
                                                                        <p className="text-[#1b1c1e] pt-1 font-bold">
                                                                            담당 픽업기사
                                                                            <span className="font-normal pl-2">
                                                                                {item.name} {item.tel}
                                                                            </span>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        );
                                                    })}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-1.5 relative">
                                        <div className={`${modify ? "w-full max-w-[600px]" : "w-full"} flex gap-1.5`}>
                                            <Input
                                                disabled
                                                onClick={() => setShowDepartPostcode(true)}
                                                className="w-full"
                                                value={depart.postcode}
                                                onChange={e => setDepart((prev: any) => ({ ...prev, postcode: e.target.value }))}
                                                error={errors?.departPostcode}
                                            />
                                            <button
                                                onClick={() => setShowDepartPostcode(true)}
                                                className="min-w-[123px] border rounded-lg border-[#1B1C1E] text-[18px] font-[500]"
                                            >
                                                우편번호
                                            </button>
                                        </div>
                                        <Input
                                            disabled
                                            onClick={() => setShowDepartPostcode(true)}
                                            className={`${modify ? "w-full max-w-[600px]" : "w-full"}`}
                                            value={depart.address}
                                            onChange={e => setDepart((prev: any) => ({ ...prev, address: e.target.value }))}
                                            error={errors?.departAddress}
                                        />
                                        <Input
                                            className={`${modify ? "w-full max-w-[600px]" : "w-full"}`}
                                            placeholder="상세주소 입력"
                                            value={depart.addressDetail}
                                            onChange={e => {
                                                setDepart((prev: any) => ({ ...prev, addressDetail: e.target.value }));
                                            }}
                                        />
                                        {errors?.departPostcode && (
                                            <p className="text-[15px] text-[#ff4242]">{errorMessages?.departPostcode}</p>
                                        )}
                                        {/* {showDepartPostcode && depart.postcode.length === 0 && ( */}
                                        {showDepartPostcode && (
                                            <div className="absolute z-[9999999] mt-[34px] top-[22px] bg-white border w-full rounded-lg shadow-lg overflow-hidden">
                                                <div className="px-6 py-5 text-2xl font-bold border-b border-gray3 flex items-center justify-between">
                                                    <p>우편번호 찾기</p>
                                                    <Close onClick={() => setShowDepartPostcode(false)} className="cursor-pointer" />
                                                </div>
                                                <DaumPostcodeEmbed className="min-h-[470px]" onComplete={handleDepartAddress} />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {pickup && pickupFiles && onPickupFilesChange && (
                                <div className="mt-3">
                                    <p className="text-[18px] font-[500] mb-1.5">첨부파일</p>
                                    <FileDropZone
                                        files={pickupFiles}
                                        onFilesChange={onPickupFilesChange}
                                        maxFiles={3}
                                        maxSizeMB={10}
                                    />
                                    <p className="text-new text-[14px] mt-2 ml-1 break-keep">
                                        물품 사진을 등록하시면 픽업 담당자가 빠르고 정확하게 물품을 찾아갈 수 있습니다.
                                    </p>
                                </div>
                            )}

                            {isDaeguGyeongbukBusanGyeongNam && (
                                <div className="mt-2">
                                    <p className="text-new mb-1">수량 {payMethodData?.count?.trim() === "" ? 0 : payMethodData?.count}개</p>
                                    <div className="grid grid-cols-3 gap-x-5 gap-y-2 responsive555:grid-cols-1">
                                        <div className="flex items-center justify-between gap-x-2 gap-y-3 responsive555:flex-col responsive555:items-start">
                                            <label htmlFor="roll" className="text-[18px]">
                                                롤 <span className="text-new text-[16px] font-normal">*</span>
                                            </label>
                                            <Input
                                                id="roll"
                                                value={depart.roll}
                                                onChange={e => {
                                                    const onlyNumber = e.target.value.replace(/[^0-9]/g, "");
                                                    setDepart((prev: any) => ({ ...prev, roll: onlyNumber }));
                                                    setErrors(prev => ({ ...prev, roll: false, box: false, sample: false }));
                                                }}
                                                placeholder="0"
                                                error={errors?.roll}
                                                className="max-w-[100px] responsive1200:max-w-full w-full responsive1200:[&_input]:text-right"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between gap-x-2 gap-y-3 responsive555:flex-col responsive555:items-start">
                                            <label htmlFor="box" className="text-[18px]">
                                                박스 <span className="text-new text-[16px] font-normal">*</span>
                                            </label>
                                            <Input
                                                id="box"
                                                value={depart.box}
                                                onChange={e => {
                                                    const onlyNumber = e.target.value.replace(/[^0-9]/g, "");
                                                    setDepart((prev: any) => ({ ...prev, box: onlyNumber }));
                                                    setErrors(prev => ({ ...prev, box: false, roll: false, sample: false }));
                                                }}
                                                placeholder="0"
                                                error={errors?.box}
                                                className="max-w-[100px] responsive1200:max-w-full w-full responsive1200:[&_input]:text-right"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between gap-x-2 gap-y-3 responsive555:flex-col responsive555:items-start">
                                            <label htmlFor="sample" className="text-[18px]">
                                                샘플 <span className="text-new text-[16px] font-normal">*</span>
                                            </label>
                                            <Input
                                                id="sample"
                                                value={depart.sample}
                                                onChange={e => {
                                                    const onlyNumber = e.target.value.replace(/[^0-9]/g, "");
                                                    setDepart((prev: any) => ({ ...prev, sample: onlyNumber }));
                                                    setErrors(prev => ({ ...prev, sample: false, box: false, roll: false }));
                                                }}
                                                placeholder="0"
                                                error={errors?.sample}
                                                className="max-w-[100px] responsive1200:max-w-full w-full responsive1200:[&_input]:text-right"
                                            />
                                        </div>
                                    </div>
                                    {errors.roll && <p className="text-red-500 mt-1 text-right">수량을 확인해주세요.</p>}
                                </div>
                            )}
                        </div>
                    </div>
                    {modify && <div className="w-full h-4 bg-[#70737C14] -my-5" />}
                    <div ref={arriveRef} className="bg-white flex-1 rounded-2xl p-10 responsive1200:p-[16px]">
                        <div className="border-b-[2px] justify-between border-black flex items-center pb-2 flex-wrap gap-y-2">
                            <div className="flex gap-4">
                                <p className="text-[28px] font-bold responsive1200:text-[20px] text-nowrap">도착지 정보</p>
                                {isArriveUseDefault || (
                                    <Button
                                        onClick={() => {
                                            setArrive((prev: any) => ({
                                                ...prev,
                                                name: "",
                                                tel: "",
                                                manager: "",
                                                mobile: "",
                                                postcode: "",
                                                address: "",
                                                addressDetail: "",
                                            }));
                                            setShowArrivePostcode(false);
                                        }}
                                    >
                                        주소리셋
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-1.5">
                                {c_code && (
                                    <Button
                                        onClick={() => {
                                            setShowAddressBook(true);
                                            addressBookTarget.current = "arrive";
                                        }}
                                        color="new"
                                    >
                                        주소록
                                    </Button>
                                )}

                                {pickup && <Button onClick={handleSameToArrive}>접수자 정보 동일</Button>}
                            </div>
                        </div>
                        <div>
                            <div
                                className={`flex items-center py-5 relative responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]`}
                                ref={containerRef}
                            >
                                <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                    상호명 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className="relative w-full">
                                    <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                        <Input
                                            placeholder="상호명을 직접 입력하거나 조회하세요."
                                            value={arrive.name}
                                            onChange={e => {
                                                const value = e.target.value;

                                                setArrive((prev: any) => ({ ...prev, name: value }));
                                                arriveAddressBookSearchMutate({ searchText: value });

                                                if (isSubmitting) {
                                                    let isInValidValue: boolean;
                                                    if (value.trim().length === 0) {
                                                        isInValidValue = true;
                                                    } else {
                                                        isInValidValue = false;
                                                    }
                                                    setErrors(prev => ({ ...prev, arriveName: isInValidValue }));
                                                } else {
                                                    setErrors(prev => ({ ...prev, arriveName: false }));
                                                }
                                            }}
                                            error={errors?.arriveName}
                                            onFocus={() => setArriveIsFocused(true)}
                                            onKeyDown={handleArriveKeyDown}
                                            className="w-full"
                                        />
                                        {errors?.arriveName && <p className="text-[15px] text-[#ff4242]">{errorMessages?.arriveName}</p>}
                                    </div>
                                    {arriveIsFocused && arrive.name.length >= 1 && arriveSearchData && arriveSearchData.length > 0 && (
                                        <div className={`absolute top-[56px] left-0 z-10 border bg-white rounded-lg shadow w-full`}>
                                            <button
                                                onClick={() => {
                                                    setArriveIsFocused(false);
                                                    setArriveFocusedIndex(-1);
                                                }}
                                                className="absolute right-0 top-0 w-6 h-6 hover:bg-black/10 flex justify-center items-center rounded-full"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                            <div className={` max-h-[400px] overflow-y-auto ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                                {arriveSearchData.map((company, index) => {
                                                    const displayInfo = `${(company as AddressItem).a_company} | ${(company as AddressItem).a_tel} | ${(company as AddressItem).a_address}`;

                                                    return (
                                                        <div
                                                            key={`${company.a_code}-${index}`}
                                                            ref={el => (arriveItemRefs.current[index] = el)}
                                                            className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-[16px] ${
                                                                arriveFocusedIndex === index ? "bg-gray-200" : "hover:bg-gray-100"
                                                            }`}
                                                            onClick={() => {
                                                                setArrive((prev: any) => ({
                                                                    ...prev,
                                                                    name: company.a_company,
                                                                    tel: company.a_tel,
                                                                    postcode: company.a_post,
                                                                    address: company.a_address,
                                                                    addressDetail: company.a_address_detail,
                                                                    t_c_code: company.a_code,
                                                                }));
                                                                setArriveIsFocused(false);
                                                            }}
                                                        >
                                                            {displayInfo}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center mb-5 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                <div className={`text-[18px] font-[500] min-w-[160px]`}>
                                    연락처 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                    <Input
                                        placeholder="연락처를 입력해주세요."
                                        className="w-full"
                                        value={arrive.tel}
                                        onChange={e => {
                                            const rawValue = e.target.value;
                                            const limitedValue = rawValue.slice(0, 13);

                                            setArrive((prev: any) => ({ ...prev, tel: limitedValue }));

                                            if (isSubmitting) {
                                                const isInvalid = limitedValue.trim().length === 0;
                                                setErrors(prev => ({ ...prev, arriveTel: isInvalid }));
                                            } else {
                                                setErrors(prev => ({ ...prev, arriveTel: false }));
                                            }
                                        }}
                                        error={errors?.arriveTel}
                                    />
                                    {errors?.arriveTel && <p className="text-[15px] text-[#ff4242]">{errorMessages?.arriveTel}</p>}
                                </div>
                            </div>
                            <div className="flex items-center mb-5 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                <div className="text-[18px] font-[500] min-w-[160px]">
                                    담당자명
                                    {((payMethodData.pickup === 1 && payMethodData.payment === 3) ||
                                        (payMethodData.pickup === 2 && payMethodData.payment === 3)) && (
                                        <span className="text-new text-[16px] font-normal"> *</span>
                                    )}
                                </div>
                                <div className={`flex flex-col gap-2 ${modify ? "w-full max-w-[440px]" : "w-full"}`}>
                                    <Input
                                        placeholder="담당자명을 입력해주세요."
                                        className={`${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                        value={arrive.manager}
                                        onChange={e => {
                                            setArrive((prev: any) => ({ ...prev, manager: e.target.value }));
                                            if (isSubmitting) {
                                                const isInvalid = e.target.value.trim().length === 0;
                                                setErrors(prev => ({ ...prev, arriveManager: isInvalid }));
                                            } else {
                                                setErrors(prev => ({ ...prev, arriveManager: false }));
                                            }
                                        }}
                                        error={errors?.arriveManager}
                                    />
                                    {errors?.arriveManager && <p className="text-[15px] text-[#ff4242]">담당자명을 입력하세요.</p>}
                                </div>
                            </div>
                            <div className="flex items-start mb-4 responsive1200:mb-0 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                <div className="text-[18px] font-[500] min-w-[160px] mt-3 responsive1200:mt-0">휴대폰 번호</div>
                                <div className="responsive1200:w-full">
                                    <Input
                                        placeholder="휴대폰 번호를 입력해주세요."
                                        className={`${modify ? "w-full max-w-[440px]" : "w-full"}`}
                                        value={arrive.mobile}
                                        onChange={e => {
                                            const formatted = formatMobileNumber(e.target.value);
                                            setArrive((prev: any) => ({ ...prev, mobile: formatted }));
                                        }}
                                    />
                                    <p className="text-new text-[14px] mt-2 ml-1 break-keep">
                                        휴대폰번호 입력시 수신자분께 배송 알림을 발송해드립니다.
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col gap-1 relative responsive1200:py-[10px]">
                                <div className="text-[18px] font-[500] min-w-[160px] mb-1 responsive1200:mb-[8px]">
                                    도착지 주소 <span className="text-new text-[16px] font-normal">*</span>
                                </div>
                                {pickup && useCode && !!infoData?.C_ADDR1 && (isArriveUseDefault || modify) && isPostcodeMatch ? (
                                    <div className="flex flex-col border rounded-lg">
                                        {[
                                            {
                                                value: "1",
                                                title: infoData.c_addr,
                                                addr1: infoData.C_ADDR1,
                                                addr2: infoData.C_ADDR2,
                                                name: infoData.dname,
                                                tel: infoData.dtel,
                                                post: infoData.C_Post,
                                            },
                                            {
                                                value: "2",
                                                title: infoData.c_raddr,
                                                addr1: infoData.C_RADDR1,
                                                addr2: infoData.C_RADDR2,
                                                name: infoData.dname1,
                                                tel: infoData.dtel1,
                                                post: infoData.C_RPost,
                                            },
                                            {
                                                value: "3",
                                                title: infoData.c_seaddr,
                                                addr1: infoData.C_SeADDR1,
                                                addr2: infoData.C_SeADDR2,
                                                name: infoData.dname2,
                                                tel: infoData.dtel2,
                                                post: infoData.C_SePost,
                                            },
                                        ]
                                            .filter(item => item.addr1)
                                            .map((item, i) => {
                                                const isSelected = modify
                                                    ? arrive.postcode === item.post &&
                                                      arrive.address === item.addr1 &&
                                                      arrive.addressDetail === item.addr2
                                                    : arrive.damCode === item.value;
                                                return (
                                                    <label
                                                        key={`arrive-${item.value}`}
                                                        className={`flex flex-col py-4 px-3 ${i < 2 ? "border-b" : ""} cursor-pointer ${
                                                            isSelected ? "" : "bg-[#f7f7f8]"
                                                        }`}
                                                        onClick={() => {
                                                            const telMap = { "1": c_tel, "2": c_rtel, "3": c_setel };
                                                            setArrive((prev: any) => ({
                                                                ...prev,
                                                                damCode: item.value,
                                                                address: item.addr1 || "",
                                                                addressDetail: item.addr2 || "",
                                                                postcode: item.post || "",
                                                                tel: telMap[item.value as "1" | "2" | "3"] ?? "",
                                                            }));
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-1">
                                                            {isSelected ? (
                                                                <Checked className="w-8 h-8 text-new" />
                                                            ) : (
                                                                <div className="w-[19px] h-[19px] border-gray-300 mx-[6.5px] rounded-full border-2" />
                                                            )}
                                                            <div className="flex flex-col">
                                                                <p className="text-[18px] font-semibold text-[#1b1c1e] pb-1">
                                                                    {item.title}
                                                                </p>
                                                                <p className="text-[#5a5c63]">
                                                                    {item.addr1} {item.addr2}
                                                                </p>
                                                                <p className="text-[#1b1c1e] pt-1 font-bold">
                                                                    담당 픽업기사
                                                                    <span className="font-normal pl-2">
                                                                        {item.name} {item.tel}
                                                                    </span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                    </div>
                                ) : (
                                    <>
                                        <div className={`${modify ? "w-full max-w-[600px]" : "w-full"} flex gap-1.5`}>
                                            <Input
                                                disabled
                                                onClick={() => setShowArrivePostcode(true)}
                                                className="w-full"
                                                value={arrive.postcode}
                                                onChange={e =>
                                                    setArrive((prev: any) => ({
                                                        ...prev,
                                                        postcode: e.target.value,
                                                    }))
                                                }
                                                error={errors?.arrivePostcode}
                                            />
                                            <button
                                                onClick={() => setShowArrivePostcode(true)}
                                                className="min-w-[123px] border rounded-lg border-[#1B1C1E] text-[18px] font-[500]"
                                            >
                                                우편번호
                                            </button>
                                        </div>
                                        <Input
                                            disabled
                                            onClick={() => setShowArrivePostcode(true)}
                                            className={`${modify ? "w-full max-w-[600px]" : "w-full"}`}
                                            value={arrive.address}
                                            onChange={e =>
                                                setArrive((prev: any) => ({
                                                    ...prev,
                                                    address: e.target.value,
                                                }))
                                            }
                                            error={errors?.arriveAddress}
                                        />
                                        <Input
                                            className={`${modify ? "w-full max-w-[600px]" : "w-full"}`}
                                            placeholder="상세주소 입력"
                                            value={arrive.addressDetail}
                                            onChange={e => {
                                                setArrive((prev: any) => ({ ...prev, addressDetail: e.target.value }));
                                                setErrors(prev => ({ ...prev, arriveAddressDetail: false }));
                                            }}
                                            error={errors?.arriveAddressDetail}
                                        />

                                        {errors?.arrivePostcode && (
                                            <p className="text-[15px] text-[#ff4242]">{errorMessages?.arrivePostcode}</p>
                                        )}
                                        {showArrivePostcode && (
                                            <div className="absolute z-[100] mt-[34px] top-[57px] bg-white border w-full rounded-lg shadow-lg overflow-hidden">
                                                <div className="px-6 py-5 text-2xl font-bold border-b border-gray3 flex items-center justify-between">
                                                    <p>우편번호 찾기</p>
                                                    <Close onClick={() => setShowArrivePostcode(false)} className="cursor-pointer" />
                                                </div>
                                                <DaumPostcodeEmbed className="min-h-[470px]" onComplete={handleArriveAddress} />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {showMonthlyWeight && (
                                <div className="flex items-center mt-4 mb-2 responsive1200:flex-col responsive1200:items-start responsive1200:gap-[8px] responsive1200:py-[10px]">
                                    <div className="text-[18px] font-[500] min-w-[160px]">
                                        월정무게입력
                                        <br className="responsive1200:hidden" />
                                        (월정료해당구간)
                                        <span className="text-new text-[16px] font-normal"> *</span>
                                    </div>
                                    <div className="flex flex-col responsive1200:w-full">
                                        <div className="flex items-center responsive1200:w-full">
                                            <Input
                                                className={`w-full ${modify ? "max-w-[120px]" : "max-w-[230px] responsive1200:max-w-full"}`}
                                                unit="kg"
                                                unitPadding="pr-10"
                                                value={payMethodData.weight || ""}
                                                onChange={e => {
                                                    const numericValue = e.target.value.replace(/[^0-9]/g, "");
                                                    const number = parseInt(numericValue || "0", 10);
                                                    if (number > 999) return;
                                                    onWeightChange?.(numericValue);
                                                    setErrors(prev => ({ ...prev, weight: false }));
                                                }}
                                                error={errors?.weight}
                                            />
                                            <p className="text-[18px] font-[500] ml-3 text-nowrap">
                                                총 무게 {(parseFloat(payMethodData.count || "0") || 0) * (parseFloat(payMethodData.weight || "0") || 0)} kg
                                            </p>
                                        </div>
                                        <p className="text-new text-[15px] leading-5 mt-2">
                                            * 월정료 사용 구간 Kg 미기재 시 건당 청구.
                                            <br />
                                            <span className="ml-3">(대구&lt;-&gt;대구, 대구&lt;-&gt;서울, 서울&lt;-&gt;대구)</span>
                                        </p>
                                        {errors?.weight && <p className="text-[15px] text-[#ff4242]">월정무게를 입력하세요.</p>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {showAddressBook &&
                        createPortal(
                            <div className="fixed inset-0 z-[200] bg-black bg-opacity-40 flex items-center justify-center">
                                <AddressBook
                                    onSelect={item => handleAddressSelect(item, addressBookTarget.current)}
                                    onClose={() => setShowAddressBook(false)}
                                />
                            </div>,
                            document.body,
                        )}
                </div>
                <Drawer>
                    <DrawerTrigger ref={drawerTriggerRef} className="absolute opacity-0 w-0 h-0" />
                    <DrawerContent className="bg-white [&_.rdp-root]:flex [&_.rdp-root]:justify-center max-h-[80dvh] overlflow-hidden">
                        <DrawerHeader>
                            <DrawerTitle hidden />
                            <DrawerDescription hidden />
                        </DrawerHeader>
                        <div className="px-[4%] pb-5 overflow-auto">{addressInfo}</div>
                    </DrawerContent>
                </Drawer>
            </>
        );
    },
);

export default AddressInfo;
